import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { formatApiError } from '../lib/errors';
import { resolveAssetUrl } from '../lib/assets';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Wallet, CreditCard, ShieldCheck, AlertTriangle, ImageOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable escrow checkout for classifieds. `ids` = array of classified ids
// (single-item "Pirkt tagad" or a full cart). onPaid(order) fires after a
// successful WALLET payment; Stripe redirects away to checkout.
export default function ClassifiedPurchaseDialog({ open, onOpenChange, ids, onPaid }) {
  const { tr } = useLanguage();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState('wallet');
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !ids || !ids.length) return;
    setLoading(true); setErr(''); setQuote(null);
    axios.post(`${API}/classified-orders/quote`, { classified_ids: ids })
      .then(({ data }) => {
        setQuote(data);
        setMethod(data.wallet_balance >= data.total ? 'wallet' : 'stripe');
      })
      .catch((e) => setErr(formatApiError(e, tr('Kļūda', 'Error', 'Ошибка'))))
      .finally(() => setLoading(false));
  }, [open, ids, tr]);

  const pay = async () => {
    setPaying(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/classified-orders`, {
        classified_ids: ids, payment_method: method, origin_url: window.location.origin,
      });
      if (data.status === 'paid') {
        toast.success(tr('Apmaksāts! Nauda tiek turēta līdz apstiprini saņemšanu.', 'Paid! Funds held until you confirm receipt.', 'Оплачено! Деньги удерживаются до подтверждения получения.'));
        onOpenChange(false);
        if (onPaid) onPaid(data.order);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      setErr(formatApiError(e, tr('Apmaksa neizdevās', 'Payment failed', 'Оплата не удалась')));
    } finally { setPaying(false); }
  };

  const canWallet = quote && quote.wallet_balance >= quote.total;
  const blocked = quote && (quote.is_own || !quote.all_fixed || !quote.all_available || quote.total < 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="classified-purchase-dialog">
        <DialogHeader><DialogTitle>{tr('Nopirkt caur KoLV', 'Buy via KoLV', 'Купить через KoLV')}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">{tr('Droša darījuma apmaksa', 'Secure escrow checkout', 'Безопасная оплата')}</DialogDescription>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#2F7E79]" /></div>
        ) : !quote ? (
          <p className="text-sm text-red-600 py-4" data-testid="purchase-error">{err || tr('Kļūda', 'Error', 'Ошибка')}</p>
        ) : (
          <div className="space-y-4">
            {/* Items */}
            <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="purchase-items">
              {quote.items.map((it) => (
                <div key={it.id} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F3F2EE] flex-shrink-0">
                    {it.image ? <img src={resolveAssetUrl(it.image)} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-[#C7C5BF]"><ImageOff className="w-5 h-5" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#121212] truncate">{it.title}</p>
                    {!it.available && <span className="text-[11px] text-red-600">{tr('vairs nav pieejams', 'no longer available', 'больше недоступно')}</span>}
                    {it.available && !it.is_fixed && <span className="text-[11px] text-amber-600">{tr('nav fiksēta cena', 'not fixed price', 'не фикс. цена')}</span>}
                  </div>
                  <div className="text-sm font-bold text-[#2F7E79] flex-shrink-0">{it.is_fixed && it.price != null ? `€${it.price.toFixed(2)}` : '—'}</div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-[#E5E4E0] pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5C5C5C]">{tr('Kopā', 'Total', 'Итого')}</span><span className="font-bold text-[#121212]" data-testid="purchase-total">€{quote.total.toFixed(2)}</span></div>
              <div className="flex justify-between text-xs text-[#8A8A8A]"><span>{tr('t.sk. KoLV komisija (10%, maksā pārdevējs)', 'incl. KoLV fee (10%, seller pays)', 'вкл. комиссия KoLV (10%)')}</span><span>€{quote.commission.toFixed(2)}</span></div>
            </div>

            <div className="flex items-start gap-2 text-xs text-[#2F7E79] bg-[#E6F2F0] rounded-xl px-3 py-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {tr('Drošs darījums: nauda tiek turēta un pārskaitīta pārdevējam tikai pēc tam, kad apstiprini preces saņemšanu.', 'Secure escrow: funds are held and released to the seller only after you confirm you received the item.', 'Безопасная сделка: деньги удерживаются и переводятся продавцу только после подтверждения получения.')}
            </div>

            {blocked ? (
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2.5" data-testid="purchase-blocked">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {quote.is_own ? tr('Nevar nopirkt savu sludinājumu.', "Can't buy your own listing.", 'Нельзя купить своё объявление.')
                  : !quote.all_available ? tr('Kāda prece vairs nav pieejama. Noņem to no groza.', 'An item is no longer available. Remove it from the cart.', 'Товар больше недоступен.')
                  : tr('Tikai fiksētas cenas preces var apmaksāt šeit. Runājamas/maiņas — caur ziņām.', 'Only fixed-price items can be paid here. Negotiable/swap — via chat.', 'Только товары с фикс. ценой.')}
              </div>
            ) : (
              <>
                {/* Payment method */}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => canWallet && setMethod('wallet')} disabled={!canWallet}
                    className={`rounded-xl border p-3 text-left transition-colors ${method === 'wallet' ? 'border-[#2F7E79] bg-[#E6F2F0]' : 'border-[#E5E4E0]'} ${!canWallet ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="pay-method-wallet">
                    <Wallet className="w-5 h-5 text-[#2F7E79] mb-1" />
                    <p className="text-sm font-semibold text-[#121212]">{tr('Maciņš', 'Wallet', 'Кошелёк')}</p>
                    <p className="text-[11px] text-[#8A8A8A]">€{quote.wallet_balance.toFixed(2)} {tr('pieejams', 'available', 'доступно')}</p>
                  </button>
                  <button type="button" onClick={() => setMethod('stripe')}
                    className={`rounded-xl border p-3 text-left transition-colors ${method === 'stripe' ? 'border-[#2F7E79] bg-[#E6F2F0]' : 'border-[#E5E4E0]'}`}
                    data-testid="pay-method-stripe">
                    <CreditCard className="w-5 h-5 text-[#2F7E79] mb-1" />
                    <p className="text-sm font-semibold text-[#121212]">{tr('Bankas karte', 'Card', 'Карта')}</p>
                    <p className="text-[11px] text-[#8A8A8A]">Stripe</p>
                  </button>
                </div>
                {!canWallet && (
                  <p className="text-[11px] text-[#8A8A8A] -mt-1">{tr('Maciņā nepietiek — maksā ar karti vai papildini maciņu.', 'Not enough in wallet — pay by card or top up.', 'Недостаточно средств — оплатите картой.')}</p>
                )}
                {err && <p className="text-sm text-red-600" data-testid="purchase-error">{err}</p>}
                <Button onClick={pay} disabled={paying} className="w-full h-11 rounded-full bg-[#2F7E79] hover:bg-[#276b66] text-white font-semibold gap-2" data-testid="confirm-purchase-btn">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : (method === 'wallet' ? <Wallet className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
                  {tr('Apmaksāt', 'Pay', 'Оплатить')} €{quote.total.toFixed(2)}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
